import {useCallback} from 'react';
import {useGlobalImmer} from 'use-immer-reactn';
import {useGlobal} from 'reactn';
import {GlobalState, Tables} from '../globals/state';
import sum from 'lodash/sum';

type UseCheckboxes = {
	changeList: ( isSection: boolean ) => Promise<GlobalState>;
	checkAll: () => void
	checkCheckbox: ( table: string, checked: boolean ) => void;
	checkboxes: Tables,
	hasChecked: boolean,
	isSections: boolean,
	lastChecked: number; // Simple tracking if a checkbox has been checked or unchecked.
	setUpdating: ( isUpdating: boolean ) => Promise<GlobalState>;
	totalCount: number;
	updating: boolean;
}

/**
 * Custom hook for interacting with the list of tables and
 * their corresponding checkboxes.
 *
 */
const useCheckboxes = (): UseCheckboxes => {
	const [ sections, setSections ] = useGlobalImmer( 'sections' );
	const [ tables, setTables ] = useGlobalImmer( 'tables' );
	const [ isSections, changeList ] = useGlobal( 'isSections' );
	const [ lastChecked, setLastChecked ] = useGlobalImmer( 'lastChecked' );
	const [ updating, setUpdating ] = useGlobal( 'updating' );

	const checkboxes = isSections ? sections : tables;
	const setCheckBoxes = isSections ? setSections : setTables;
	const hasChecked = !! Object.keys( checkboxes ).filter( table => checkboxes[ table ].checked ).length;
	const totalCount = sum( Object.keys( checkboxes ).map( table => {
		return checkboxes[ table ].checked ? checkboxes[ table ].count : 0;
	} ) );

	const checkCheckbox = useCallback( ( table: string, checked: boolean ) => {
		setCheckBoxes( draft => {
			draft[ table ].checked = checked;
			draft[ table ].status = 'untouched';
		} );
		setLastChecked( last => last + 1 );
	}, [ setLastChecked, setCheckBoxes ] );

	const checkAll = useCallback( () => {
		setCheckBoxes( draft => {
			Object.keys( checkboxes ).forEach( checkbox => {
				draft[ checkbox ].checked = ! hasChecked;
			} );
		} );
		setLastChecked( last => last + 1 );
	}, [ checkboxes, hasChecked, setCheckBoxes, setLastChecked ] );

	return {
		changeList,
		checkAll,
		checkCheckbox,
		checkboxes,
		hasChecked,
		isSections,
		lastChecked,
		totalCount,
		updating,
		setUpdating,
	};
};

export default useCheckboxes;
